import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { FlaerLogo } from '@/components/brand/FlaerLogo';
import { ArrowRight } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';

interface CounterProps {
  end: number;
  suffix?: string;
  prefix?: string;
  duration?: number;
}

const AnimatedCounter = ({ end, suffix = '', prefix = '', duration = 2000 }: CounterProps) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const startTime = performance.now();
          
          const animate = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easeOut = 1 - Math.pow(1 - progress, 4);
            setCount(Math.floor(end * easeOut));
            
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCount(end);
            }
          };
          
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return (
    <span ref={ref} className="mono counter-animate">
      {prefix}{count}{suffix}
    </span>
  );
};

export function HeroSection() {
  return (
    <>
      {/* Navigation */}
      <nav className="relative z-10 border-b border-border/30 bg-background/60 backdrop-blur-xl">
        <div className="max-w-[1080px] mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <FlaerLogo size="sm" />
            <div className="flex items-center gap-3">
              <Link to="/pricing">
                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                  Pricing
                </Button>
              </Link>
              <Link to="/auth">
                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                  Sign In
                </Button>
              </Link>
              <Link to="/auth">
                <Button size="sm">
                  Start Trading
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 pt-24 pb-20 px-6">
        <div className="max-w-[1080px] mx-auto">
          {/* Badge */}
          <div className="mb-8">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-foreground animate-pulse" />
              For serious traders only
            </span>
          </div>
          
          {/* Headline */}
          <h1 className="text-5xl sm:text-6xl lg:text-8xl font-black tracking-tight-custom mb-6 leading-[0.95]">
            Discipline Wins.
          </h1>
          
          {/* Subhead */}
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mb-10 leading-relaxed">
            FLAER is autonomous trading intelligence for the Indian market. 
            It generates signals, manages risk, and executes when you're ready. 
            No noise. No guesswork. Just edge.
          </p>
          
          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-start gap-4 mb-16">
            <Link to="/auth">
              <Button size="lg" className="text-base px-8">
                Start Paper Trading
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/learn" className="text-muted-foreground hover:text-foreground transition-colors text-sm flex items-center gap-2 py-3">
              See How It Works
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          {/* Trust Strip */}
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold mono"><AnimatedCounter end={73} suffix="%" /></span>
              <span className="text-muted-foreground">hit rate</span>
            </div>
            <span className="text-border">|</span>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold mono"><AnimatedCounter end={10} suffix="K+" /></span>
              <span className="text-muted-foreground">signals analyzed</span>
            </div>
            <span className="text-border hidden sm:block">|</span>
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-2xl font-bold mono">2.1:1</span>
              <span className="text-muted-foreground">reward-to-risk</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
